import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import { useRouter } from '@core/utils/utils'
import { ref, computed } from '@vue/composition-api'
import i18n from '@/libs/i18n'
import awsConnection from '@/views/habit/aws';
import useCommon from '@/views/organization/useCommon'
import useCommonDashboards from '@/views/habit/useCommonDashboards'
import useCommonTodo from '@/views/apps/todo/useCommonTodo'
import { get, set } from 'idb-keyval'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import { probabilityValues, impactValues } from '@/constants'
import { hasPermission } from '@/composables/usePermission'

// respondControlIndex: cuando viene definido, el composable entra en "modo respuesta individual"
// (link de mail a un responsable de control): carga un solo control de una confirmación existente
// en vez del flujo completo de evaluación, valida que el usuario logueado sea el responsable
// asignado y que el control no haya sido respondido todavía.
export default function useConfirmationForm(isNewConfirmation, isReEvaluation = false, respondControlIndex = null) {
  const userData = store.state?.userStore?.userData
  const clientId = userData.role !== 'admin' ? userData.client.$oid : null
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError, storeDataToUpload, formatDate, updateConfirmationWithKey, createImprovements, createCase, calculateNewRR, getRiskCategoryFromNumeric } = useCommon()
  const { router, route } = useRouter()
  const { singleUpload } = awsConnection()
  const { sumTimes } = useCommonDashboards()
  const { sendImprovementEmail, sendPresenceValidationEmail, sendControlResponsibleEmail, sendScheduledEvaluationEmail, uploadFileToS3 } = useCommonTodo()
  const { getItem, getItems, getItemsAggregate, createItem, updateItem } = useLambda()

  const continueConfirmation = ref(!isNewConfirmation || isReEvaluation)
  const time = ref("00:00:00")
  const timeBegan = ref(null)
  const interval = ref(null)
  const isOnline = computed(() => store.state.app.isOnline)

  const process = ref({})
  const sampleSizes = computed(() =>
    (process.value.activities || []).map(control => getSampleSize(process.value.inherentRisk, control.population))
  )
  const event = ref({})
  const selectedWorker = ref('')
  const selectedSupervisor = ref('')
  const metadataVisible = ref(route.value.params.event || route.value.params.user ? true : false)
  const { default_view_pc_team, default_view_daily_dialogue, new_confirmation_date_variable } = JSON.parse(localStorage.getItem("clientData") || '{}')

  const isSubmitting = ref(false)
  const isSendingPresenceValidationEmail = ref(false)
  const isPresenceValidationEmailSent = ref(false)
  const isValidatingPresence = ref(false)
  const isPresenceValidated = ref(false)
  const improvements = ref([])
  const commitments = ref([])
  const filesToUpload = ref([])
  const presenceValidationId = ref(null)
  const now = new Date()
  const generalConclusion = ref ('')
  const isOpeningCaseForm = ref(false)
  const emailSummary = ref(null)
  const showEmailSummaryModal = ref(false)
  const respondAccessDenied = ref(false)
  const respondAlreadyCompleted = ref(false)
  const isReloadingControlsStatus = ref(false)
  let tempEvaluationCreated = false
  let pendingEditNavigation = false

  // if (userData.role === 'supervisor' || userData.role === 'consultant') {
  //   selectedSupervisor.value = userData.worker_id.$oid
  // }

  let selectWorkerDisabled = false
  const selectSupervisorDisabled = ref(!isNewConfirmation)
  const selectDateDisabled = ref(isNewConfirmation ? false : !JSON.parse(localStorage.getItem("clientData") || "{}").new_confirmation_date_variable)

  if (route.value.params.user) {
    // continueConfirmation.value = true
    // selectSupervisorDisabled.value = true
    selectDateDisabled.value = true
    if (route.value.params.user !== "worker") {
      selectWorkerDisabled = true
      selectedWorker.value = route.value.params.user
    }
  }
  event.value.date = new Date()

  // Info del proceso a incluir en el mail de notificación (código, nombre, riesgo inherente y
  // residual) — mismos datos que se muestran en la lista de procesos.
  const getProcessInfoForEmail = () => ({
    name: process.value.name,
    code: process.value.code,
    inherentRisk: process.value.inherentRisk,
    residualRisk: process.value.residualRisk,
  })

  // Envía el mail de notificación a cada responsable con control asignado (bloqueado en este
  // momento) y arma el resumen de 4 listas (enviados / error de envío / sin mail / sin responsable)
  // que se muestra en el modal. Se usa tanto al "Continuar con evaluación" (todos los controles ya
  // bloqueados) como al guardar un responsable individual desde "Guardar y enviar email" (un control).
  // Recibe pares { control, index }, no un array plano, para que el índice sea siempre la
  // posición real dentro de confirmation.activities (necesaria para el link del mail y para mostrar
  // el número de control) y no la posición dentro del array que se le pase.
  const sendControlResponsibleEmails = async (controlsWithIndex, confirmationId, processInfo) => {
    const summary = { sent: [], sendError: [], noEmail: [], noResponsible: [], skipped: [] }

    await Promise.all(controlsWithIndex.map(async ({ control, index }) => {
      if (!control.responsible_id) {
        summary.noResponsible.push({ control: control.name, index })
        return
      }

      if (control.skipNotification) {
        summary.skipped.push({ control: control.name, index })
        return
      }

      const result = await sendControlResponsibleEmail({
        responsibleWorkerId: oid(control.responsible_id),
        controlName: control.name,
        controlIndex: index,
        population: parseInt(control.population || '0', 10),
        sample: sampleSizes.value[index] || 0,
        processInfo,
        confirmationId,
      })

      const entry = { control: result.control, index, responsible: result.responsible || '', email: result.email }

      if (result.sent) summary.sent.push(entry)
      else if (result.sendFailed) summary.sendError.push(entry)
      else summary.noEmail.push(entry)
    }))

    // Promise.all resuelve en orden de finalización de red, no en el orden de los controles:
    // hay que reordenar cada lista por índice para que el modal siempre muestre 1, 2, 3...
    const byIndex = (a, b) => a.index - b.index
    summary.sent.sort(byIndex)
    summary.sendError.sort(byIndex)
    summary.noEmail.sort(byIndex)
    summary.noResponsible.sort(byIndex)
    summary.skipped.sort(byIndex)

    return summary
  }

  // Crea (una sola vez, ver tempEvaluationCreated) una confirmación borrador apenas arranca la
  // evaluación, para tener un confirmationId real desde el inicio y dejar guardado qué responsable
  // quedó seleccionado para cada control en ESTE momento (sin tocar control.responsible_id)
  const startEvaluation = async () => {
    if (!isNewConfirmation || tempEvaluationCreated) return true

    isSubmitting.value = true

    try {
      const payload = {
        client_id: oid(userData.client.$oid),
        process: oid(process.value._id),
        worker: oid(selectedWorker.value),
        supervisor: oid(selectedSupervisor.value),
        date: date(formatDate(event.value.date)),
        duration: time.value,
        activities: process.value.activities?.map((control, index) => ({
          name: control.name,
          population: parseInt(control.population || '0', 10),
          sample: sampleSizes.value[index] || 0,
          responsible_id: control.responsible_id ? oid(control.responsible_id) : null,
          // Bloqueado desde ya en la base si al momento de "Continuar" ya tenía responsable asignado
          responsibleLocked: !!control.responsible_id,
          skipNotification: !!control.skipNotification,
          behaviours: control.behaviours?.map(behaviour => ({
            name: behaviour.name,
            type: behaviour.type,
            answer: '',
            expectedAnswer: behaviour.expectedAnswer,
            comment: '',
            evidenceKey: [],
          })) || [],
        })) || [],
        pending: true,
      }

      const { insertedId } = await createItem({ collection: 'confirmation', payload })
      process.value.confirmationId = insertedId.toString()
      tempEvaluationCreated = true

      // A partir de acá, los controles que ya tenían responsable quedan bloqueados (solo lectura),
      // ya persistido en confirmation.activities[].responsibleLocked (no depende de estado del cliente).
      // Los que quedaron sin responsable siguen editables (se guardan/bloquean uno a uno después, con
      // el botón "Guardar y enviar mail" vía saveControlResponsible).
      const applyToBaseControlPromises = []

      process.value.activities?.forEach(control => {
        control.responsibleLocked = !!control.responsible_id

        if (control.applyToBaseControl && control.responsible_id && control.responsible_id !== control._initialResponsibleId) {
          applyToBaseControlPromises.push(
            updateItem({
              collection: 'control',
              query: { _id: oid(control._id) },
              action: { $set: { responsible_id: oid(control.responsible_id) } },
            })
          )
        }
      })

      // Mismo flujo que "Guardar Borrador" para que la evaluación aparezca en el calendario /
      // "Confirmaciones pendientes" desde este momento, pero sin navegar a otra pantalla.
      // Corre en paralelo con las actualizaciones de "control base": ninguna depende de la otra.
      await Promise.all([
        updateEventWithConfirmation(process.value.confirmationId, true, { navigate: false }),
        ...applyToBaseControlPromises,
      ])

      if (process.value.activities?.length) {
        const controlsWithIndex = process.value.activities.map((control, index) => ({ control, index }))
        emailSummary.value = await sendControlResponsibleEmails(controlsWithIndex, process.value.confirmationId, getProcessInfoForEmail())
        showEmailSummaryModal.value = true
      }

      return true
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.evaluation_start_error'))

      return false
    } finally {
      isSubmitting.value = false
    }
  }

  // Guarda (y en el futuro dispara el mail) el responsable de UN control puntual que quedó sin
  // asignar al momento de "Continuar" y recién ahora se completa. Update dirigido solo a esa
  // activity (no reescribe el array completo). El bloqueo queda persistido en
  // confirmation.activities[].responsibleLocked, no depende de estado transitorio del cliente.
  // Usa un loading local (control.isSavingResponsible), no el isSubmitting global de toda la pantalla.
  const saveControlResponsible = async (control, controlIndex) => {
    if (!control.responsible_id) return

    control.isSavingResponsible = true

    try {
      const query = { _id: oid(process.value.confirmationId) }
      const action = {
        $set: {
          [`activities.${controlIndex}.responsible_id`]: oid(control.responsible_id),
          [`activities.${controlIndex}.responsibleLocked`]: true,
        }
      }

      await updateItem({ collection: 'confirmation', query, action })

      if (control.applyToBaseControl && control.responsible_id !== control._initialResponsibleId) {
        await updateItem({
          collection: 'control',
          query: { _id: oid(control._id) },
          action: { $set: { responsible_id: oid(control.responsible_id) } },
        })
      }

      control.responsibleLocked = true
      showSuccessMessage(i18n.t('message.control_responsible_saved'))

      emailSummary.value = await sendControlResponsibleEmails([{ control, index: controlIndex }], process.value.confirmationId, getProcessInfoForEmail())
      showEmailSummaryModal.value = true
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.control_responsible_save_error'))
    } finally {
      control.isSavingResponsible = false
    }
  }

  const enableConfirmation = async () => {
    const started = await startEvaluation()
    if (!started) return

    // La ruta "new" deja de tener sentido una vez que ya existe una confirmación real: si el
    // usuario refresca, se perdería la referencia (volvería a armar todo desde cero). Navegamos
    // directamente (replace, no push, para no ensuciar el historial) a la ruta "edit" de la
    // confirmación recién creada, que sí es refresh-safe. Mantenemos el loading (isSubmitting)
    // en vez de revelar el formulario completo en esta instancia de camino a desmontarse, para
    // no mostrar un parpadeo del formulario "new" justo antes de navegar.
    if (isNewConfirmation && process.value.confirmationId) {
      // Si se mostró el modal de resumen de mails, esperamos a que el usuario lo cierre
      // (acknowledgeEmailSummary) antes de navegar, para que llegue a verlo. No tocamos
      // isSubmitting acá (mismo criterio que presence-validation-modal): el backdrop del modal
      // ya cubre el formulario de atrás, y superponer el loading spinner generaba un choque visual.
      if (showEmailSummaryModal.value) {
        pendingEditNavigation = true
        return
      }

      isSubmitting.value = true
      router.replace({ name: 'habit-confirmation-edit', params: { id: process.value.confirmationId } })
      return
    }

    continueConfirmation.value = true
    // selectSupervisorDisabled.value = true
    selectDateDisabled.value = !new_confirmation_date_variable
    !new_confirmation_date_variable ? (event.value.date = new Date()) : null;
    // selectedSupervisor.value = userData.worker_id?.$oid
    metadataVisible.value = !metadataVisible.value
    startTimer()
  }

  // Cierre del modal de resumen de notificación a responsables. Si quedó pendiente la navegación
  // a "edit" (caso "Continuar con evaluación de inmediato"), se dispara recién acá.
  const acknowledgeEmailSummary = () => {
    showEmailSummaryModal.value = false

    if (pendingEditNavigation) {
      pendingEditNavigation = false
      isSubmitting.value = true
      router.replace({ name: 'habit-confirmation-edit', params: { id: process.value.confirmationId } })
    }
  }

  const activatePresenceValidation = async () => {
    if (!selectedWorker.value) return showErrorMessage(i18n.t('message.must_select_evaluated'))

    isSendingPresenceValidationEmail.value = true

    try {
      const payload = {
        client_id: oid(userData.client.$oid),
        process: typeof process.value._id === 'string' ? oid(process.value._id) : process.value._id,
        worker: oid(selectedWorker.value),
        supervisor: selectedSupervisor.value ? oid(selectedSupervisor.value) : null,
        date: date(formatDate(event.value.date)),
        validated: false,
      }

      const { insertedId } = await createItem({ collection: 'presence_validation', payload })
      presenceValidationId.value = insertedId

      // Send email to evaluated to validate presence
      payload.presenceValidationId = insertedId.toString()
      payload.process = process.value.name
      payload.date = formatDate(event.value.date)
      await sendPresenceValidationEmail(payload)

      isPresenceValidationEmailSent.value = true
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.err_send_presence_validation_email'))
    } finally {
      isSendingPresenceValidationEmail.value = false
    }
  }

  const validatePresence = async () => {
    isValidatingPresence.value = true

    try {
      const query = { _id: oid(presenceValidationId.value) }
      const item = await getItem({ collection: 'presence_validation', query })

      if (item?.validated) {
        showSuccessMessage(i18n.t('message.presence_validated'))
        isPresenceValidated.value = true
      } else {
        showErrorMessage(i18n.t('message.presence_not_validated'))
      }
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.err_validate_presence'))
    } finally {
      isValidatingPresence.value = false
    }
  }

  // Carga UN control de una confirmación existente (link de mail al responsable). No reutiliza el
  // resto de show() porque las validaciones y la forma de armar process.value son muy distintas
  // (un solo control, chequeo de identidad, chequeo de "ya respondido") del resto de los modos.
  const loadControlToRespond = async () => {
    isSubmitting.value = true

    try {
      const confirmationId = route.value.params.id
      const query = { _id: oid(confirmationId) }

      const pipeline = [
        { $match: query },
        // processId se calcula ANTES del $lookup que sobreescribe "process" con el objeto
        // buscado, en una etapa separada, para no depender del orden de evaluación de campos
        // dentro de un mismo $addFields.
        { $addFields: { processId: { $toString: "$process" } } },
        { $lookup: { from: 'process', localField: 'process', foreignField: '_id', pipeline: [ { $project: { name: 1, code: 1, inherentRisk: 1, residualRisk: 1 } } ], as: 'processData' } },
        {
          $addFields: {
            process: { $arrayElemAt: ["$processData", 0] },
            activities: {
              $map: {
                input: "$activities",
                as: "activity",
                in: {
                  name: "$$activity.name",
                  behaviours: "$$activity.behaviours",
                  responsible_id: { $toString: "$$activity.responsible_id" },
                  completed: { $ifNull: ["$$activity.completed", false] },
                }
              }
            }
          }
        }
      ]

      const items = await getItemsAggregate({ collection: 'confirmation', pipeline })
      const confirmation = items?.[0]
      if (!confirmation) throw new Error('Confirmation not found')

      const control = confirmation.activities?.[respondControlIndex]
      if (!control) throw new Error('Control not found')

      const loggedWorkerId = userData?.worker_id?.$oid
      if (!loggedWorkerId || control.responsible_id !== loggedWorkerId) {
        respondAccessDenied.value = true
        return
      }

      if (control.completed) {
        respondAlreadyCompleted.value = true
        return
      }

      process.value = {
        name: confirmation.process?.name,
        code: confirmation.process?.code,
        inherentRisk: confirmation.process?.inherentRisk,
        residualRisk: confirmation.process?.residualRisk,
        processId: confirmation.processId,
        confirmationId,
        activities: [control],
      }
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.process_fetch_error') })
    } finally {
      isSubmitting.value = false
    }
  }

  // Envía la respuesta del único control de este modo: guarda sus behaviours dentro de
  // confirmation.activities[respondControlIndex] y lo marca como completed. A diferencia de
  // postConfirmation, no toca el resto de la confirmación ni recalcula el RR (es la respuesta de
  // UN control, no el cierre de la evaluación completa).
  const submitControlResponse = async () => {
    const control = process.value.activities?.[0]
    if (!control) return

    const allBehavioursAnswered = control.behaviours?.every(behaviour => !!behaviour.answer)
    if (!allBehavioursAnswered) {
      showErrorMessage(i18n.t('message.allBehavioursMustBeAnswered'))
      return
    }

    isSubmitting.value = true

    try {
      // Subir evidencia adjunta a S3 (mismo flujo que postConfirmation/submit, pero acotado a
      // los behaviours de este único control)
      control.behaviours?.forEach((behaviour, behaviourIndex) => {
        if (behaviour.file?.length) {
          behaviour.file.forEach(file => {
            filesToUpload.value.push({
              file,
              clientId: userData.client.$oid,
              riskId: process.value.processId,
              controlIndex: respondControlIndex,
              behaviourIndex,
            })
          })
        }
      })

      if (filesToUpload.value.length) {
        const uploadFilesPromises = filesToUpload.value.map(e => {
          const destinationFolder = `${e.clientId}/${e.riskId}/confirmation/${e.controlIndex}/${e.behaviourIndex}`
          return new Promise(resolve => {
            uploadFileToS3(e.file, destinationFolder)
              .then(key => resolve({ key, behaviourIndex: e.behaviourIndex }))
              .catch(err => {
                console.log(err)
                showErrorMessage(i18n.t('message.evidence_upload_error'))
                resolve(null)
              })
          })
        })

        const newFileKeys = await Promise.all(uploadFilesPromises)

        for (const k of newFileKeys) {
          if (!k) continue
          const behaviour = control.behaviours[k.behaviourIndex]
          if (behaviour.evidenceKey?.length) behaviour.evidenceKey.push(k.key)
          else behaviour.evidenceKey = [k.key]
        }
      }

      const query = { _id: oid(process.value.confirmationId) }
      const action = {
        $set: {
          [`activities.${respondControlIndex}.behaviours`]: control.behaviours.map(behaviour => ({
            name: behaviour.name,
            type: behaviour.type,
            answer: behaviour.answer?.toString() || '',
            expectedAnswer: behaviour.expectedAnswer,
            comment: behaviour.comment || '',
            evidenceKey: behaviour.evidenceKey || [],
          })),
          [`activities.${respondControlIndex}.completed`]: true,
        }
      }

      await updateItem({ collection: 'confirmation', query, action })

      showSuccessMessage(i18n.t('message.control_response_submitted'))
      respondAlreadyCompleted.value = true
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.control_response_submit_error'))
    } finally {
      isSubmitting.value = false
    }
  }

  // Botón "Recargar evaluaciones" (vista Edit): a diferencia de show(), NO reemplaza
  // process.value entero (evaluador/evaluado/fecha/metadata/población/muestra/collapsed quedan
  // como estaban) y no usa isSubmitting (nada de loading general de pantalla completa). Solo trae
  // de la base los behaviours respondidos y el estado (completed/responsibleLocked) de cada
  // control, y los mergea in-place en los controles que ya están en memoria.
  const reloadControlsStatus = async () => {
    if (!process.value.confirmationId) return

    isReloadingControlsStatus.value = true

    try {
      const query = { _id: oid(process.value.confirmationId) }
      const pipeline = [
        { $match: query },
        { $project: { activities: 1 } },
      ]

      const items = await getItemsAggregate({ collection: 'confirmation', pipeline })
      const freshActivities = items?.[0]?.activities

      if (!freshActivities?.length) return

      process.value.activities?.forEach((control, index) => {
        const fresh = freshActivities[index]
        if (!fresh) return

        control.completed = fresh.completed || false
        control.responsibleLocked = fresh.responsibleLocked || false

        fresh.behaviours?.forEach((freshBehaviour, behaviourIndex) => {
          const behaviour = control.behaviours?.[behaviourIndex]
          if (!behaviour) return

          behaviour.answer = freshBehaviour.answer
          behaviour.comment = freshBehaviour.comment
          behaviour.evidenceKey = freshBehaviour.evidenceKey
        })
      })
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.reload_controls_status_error'))
    } finally {
      isReloadingControlsStatus.value = false
    }
  }

  const show = async () => {
    if (respondControlIndex != null) {
      await loadControlToRespond()
      return
    }

    if (isOnline.value) {
      isSubmitting.value = true

      if (isNewConfirmation && !isReEvaluation) {
        try {
          const pipeline = getPipeline()
        
          const items = await getItemsAggregate({ collection: 'process', pipeline })

          if (items?.[0]?.activities?.length) {
            items[0].activities.forEach(activity => {
              activity.population = ''
            })
          }
    
          process.value = items[0] || {}

          if (!selectedWorker.value && process.value.evaluated?.[0]?._id && !process.value.evaluated[0].deleted) selectedWorker.value = String(process.value.evaluated[0]._id)
          
          // In forms (code starts with 'F') the evaluator is the same as the evaluated
          if (process.value.code?.startsWith('F')) selectedSupervisor.value = selectedWorker.value
          else if (!selectedSupervisor.value && process.value.evaluator?.[0]?._id && !process.value.evaluator[0].deleted) selectedSupervisor.value = String(process.value.evaluator[0]._id)
        } catch (error) {
          console.log(error)
          showErrorMessage(i18n.t('message.process_list_fetch_error'))
        } finally {
          isSubmitting.value = false
        }
      } else {
        try {
          // Fetch confirmation data
          const query = { _id: oid(route.value.params.id) }
          if (clientId) query.client_id = oid(clientId)

          const pipeline = [
            { $match: query },
            { $lookup: { from: 'process', localField: 'process', foreignField: '_id', pipeline: [ { $project: { name: 1, code: 1, goal: 1, description: 1, requireImage: 1, inherentRisk: 1, residualRisk: 1, allFileKeys: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ], as: 'process' } },
            { $addFields: { _id: { $toString: "$_id" }, worker: { $toString: "$worker" }, supervisor: { $toString: "$supervisor" }, process: { $arrayElemAt: ["$process", 0] } } }
          ]
        
          const items = await getItemsAggregate({ collection: 'confirmation', pipeline })
          if (!items?.[0]) throw new Error('Item not found')
  
          const oldConfirmation = items[0] || {}
  
          process.value = {
            ...oldConfirmation.process,
            metadata: oldConfirmation.metadata,
            activities: oldConfirmation.activities,
            duration: oldConfirmation.duration,
            confirmationId: oldConfirmation._id,
            allFileKeys: oldConfirmation.allFileKeys,
          }
  
          selectedWorker.value = oldConfirmation.worker || ''
          selectedSupervisor.value = oldConfirmation.supervisor || ''
          event.value.date = oldConfirmation.date
          isPresenceValidated.value = isReEvaluation ? false : oldConfirmation.presenceValidated || false
          generalConclusion.value = oldConfirmation.generalConclusion || ''
        } catch (error) {
          console.log(error)
          handleError({ error, defaultMessage: i18n.t('message.process_fetch_error') })
        } finally {
          isSubmitting.value = false
        }
      }
    } else {
      get('processesComplete').then(response => {
        if (response) {
          process.value = response.find(e => e._id === route.value.params.id)
          if (!process.value) handleError({ isStorageError: true })
        }
        else if (localStorage.processesComplete) {
          process.value = JSON.parse(localStorage.processesComplete).find(e => e._id === route.value.params.id)
          if (!process.value) handleError({ isStorageError: true })
        }
        else handleError({ isStorageError: true })
      })
    }
  }

  const schedulleEvent = async (calendar, confirmationId, isDraft, { navigate = true } = {}) => {
    const startDateTime = formatDate(event.value.date)
    const endDateTime = formatDate(event.value.date, 13)
    // If an confirmationId exists, try to looking for an event with extendedProps.calendar != "Realizadas" and update that event instead 
    // of create a new event in the calendar. If all the events for the attendee have a confirmationId and his prop 
    // extendedProps.calendar == "Realizadas" continue with the code and create a event.
    if (confirmationId) {
      const firstDayOfCurrentMonth = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), 1)
      const firstDayOfNextMonth = new Date(startDateTime.getFullYear(), startDateTime.getMonth() + 1, 1)
      const query = {
        'extendedProps.calendar': { $ne: "Realizadas" },
        start: { $gte: date(firstDayOfCurrentMonth), $lt: date(firstDayOfNextMonth) },
        attendee: selectedWorker.value ? oid(selectedWorker.value) : null,
        process: oid(process.value._id),
        deleted: { $ne: true },
        $or: [{ confirmation: { $exists: false } }, { confirmation: oid(confirmationId) }],
      }
      try { 
        const eventsToSearch = await getItems({ collection: 'event', query, options: { projection : { improvements: 1 }, sort: { start: 1 } } })

        if (eventsToSearch?.length) {
          const updateQuery = { _id: eventsToSearch[0]._id }
          const updatePayload = {
            extendedProps: { calendar: "Realizadas"},
            start: date(startDateTime),
            end: date(endDateTime),
            confirmation: oid(confirmationId)
          }

          // If there are new improvements, create improvements and merge them with old improvements from the event if there are any
          // if (improvements.value.length) {
          //   try {
          //     const newImprovementIds = await createImprovements(improvements.value, "event", eventExist[0]._id)
          //     const oldImprovementIds = eventExist[0].improvements?.map(e => e._id) || []
          //     queryDos.variables.data.improvements = {
          //       link: [...oldImprovementIds, ...newImprovementIds]
          //     }
          //   } catch (error) {
          //     console.log(error)
          //   }
          // }

          const action = { $set: updatePayload }

          await updateItem({ collection: 'event', query: updateQuery, action })

          if (navigate) {
            if (isDraft) router.push({ name: 'organization-pending-confirmations' })
            else router.push({ name: 'habit-confirmation-view', params: { id: confirmationId } })
          }
          return eventsToSearch[0]._id
        }
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.event_update_error'))
      }
    }

    if (!selectedSupervisor.value) {
      showErrorMessage(i18n.t('message.evaluatorRequired'))
      return
    }

    isSubmitting.value = true

    const payload = {
      client_id: userData.client.$oid,
      process: process.value._id,
      attendee: selectedWorker.value,
      organizer: selectedSupervisor.value,
      start: date(startDateTime),
      end: date(endDateTime),
      confirmation: confirmationId,
      extendedProps: {
        calendar: calendar || (startDateTime < now ? 'Vencidas' : 'Futuras'),
      },
    }

    // if (improvements.value.length) {
    //   // Upload documents to AWS and then store document keys in an array
    //   const uploadDocumentPromises = improvements.value.map(i => {
    //     if (!i.documentFile?.name) return null
    //     const destinationFolder = `${userData.client.$oid}/documents`
    //     return new Promise((resolve, reject) => {
    //       singleUpload(i.documentFile, destinationFolder)
    //         .then((key) => resolve(key))
    //         .catch((err) => {
    //           console.log(err)
    //           resolve(null)
    //         })
    //     })
    //   })
    //   const documentKeys = await Promise.all(uploadDocumentPromises)

    //   // Set improvements property
    //   payload.improvements = {
    //     create: improvements.value.map((c, index) => {
    //       const newImprovement  = {
    //         client_id: c.client_id,
    //         assignee: c.assignee ? { link: c.assignee._id } : null,
    //         note: c.note,
    //         tags: c.tags,
    //         description: c.description,
    //         dueDate: c.dueDate ? new Date(`${c.dueDate.slice(0, 10)} 12:00:00`) : null,
    //         important: c.important,
    //       }
    //       if (c.documentFile?.name) newImprovement.imageKey = documentKeys[index]
    //       if (c.metadata?.length) {
    //         newImprovement.metadata = c.metadata.map(m => ({
    //           name: m.name,
    //           type: m.type,
    //           answer: m.answer,
    //         }))
    //       }
    //       if (c.associatedCase) {
    //         newImprovement.associatedCase = { link: c.associatedCase }
    //       }
    //       return newImprovement
    //     })
    //   }
    // }

    if (isOnline.value) {
      return postEvent(payload, confirmationId, isDraft, { navigate })
    }
    else {
      storeDataToUpload('eventsToUpload', payload)
      // let eventsStored = localStorage.eventsForSchedule ? JSON.parse(localStorage.eventsForSchedule) : []
      // eventsStored.push({...payload})
      // localStorage.eventsForSchedule = JSON.stringify(eventsStored)
      showSuccessMessage(i18n.t('message.form_stored'))
      if (navigate) {
        if(default_view_pc_team) router.push({ name: 'apps-pc-team' })
        else if(default_view_daily_dialogue) router.push({ name: 'habit-meeting-new' })
        else router.push({ name: 'apps-calendar' })
      }
    }
  }

  const postEvent = async (payload, confirmationId, isDraft, { navigate = true } = {}) => {
    payload.client_id = oid(payload.client_id)
    payload.process = oid(payload.process)
    payload.attendee = oid(payload.attendee)
    payload.organizer = oid(payload.organizer)
    payload.confirmation = oid(payload.confirmation)

    try {
      const { insertedId } = await createItem({ collection: 'event', payload })
      showSuccessMessage(i18n.t('message.confirmation_agenda'))

      if (navigate) {
        if (isDraft) router.push({ name: 'organization-pending-confirmations' })
        else if (confirmationId) router.push({ name: 'habit-confirmation-view', params: { id: confirmationId } })
        else {
          if (default_view_pc_team) router.push({ name: 'apps-pc-team' })
          else if (default_view_daily_dialogue) router.push({ name: 'habit-meeting-new' })
          else router.push({ name: 'apps-calendar' })
        }
      }

      return insertedId
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.no_confirmation_agenda'))
      isSubmitting.value = false
    }
  }

  const updateEventWithConfirmation = async (confirmationId, isDraft, { navigate = true } = {}) => {
    const eventId = route.value.params.event
      ? route.value.params.event
      : isNewConfirmation
        ? null
        : await getEventId(confirmationId)
    const processId = isNewConfirmation ? route.value.params.id : null
    const statusEvent = isDraft ? "Futuras" : "Realizadas"

    if (!confirmationId || !eventId) {
      return schedulleEvent(statusEvent, confirmationId, isDraft, { navigate })
    }

    const payload = {
      confirmation: oid(confirmationId),
      extendedProps: { calendar: statusEvent },
      attendee: oid(selectedWorker.value),
      organizer: oid(selectedSupervisor.value),
    }

    if (processId) payload.process = oid(processId)

    // Actualizar fecha del evento siempre (draft o final) + evaluador/evaluado
    const startDateTime = formatDate(event.value.date)
    const endDateTime = formatDate(event.value.date, 13)

    payload.start = date(startDateTime)
    payload.end = date(endDateTime)

    // If there are new improvements, create improvements and get event to see if it already has improvements to merge them
    // if (improvements.value.length) {
    //   try {
    //     const newImprovementIds = await createImprovements(improvements.value, "event", eventId)
  
    //     const eventResponse = await axios.post('/graphql', { query: queryEvent, variables: { query: { _id: eventId } } })
    //     if (eventResponse.data?.errors) throw new Error(eventResponse.data.errors[0].message)
    //     const oldImprovementIds = eventResponse.data?.data?.event?.improvements?.map(e => e._id) || []
    //     payload.improvements = {
    //       link: [...oldImprovementIds, ...newImprovementIds]
    //     }
    //   } catch (error) {
    //     console.log(error)
    //   }
    // }

    const collection = 'event'
    const query = { _id: oid(eventId) }
    const action = { $set: payload }

    try {
      await updateItem({ collection, query, action })

      showSuccessMessage(i18n.t('message.Event_updated'))

      if (navigate) {
        if (isDraft) router.push({ name: 'organization-pending-confirmations' })
        else router.push({ name: 'habit-confirmation-view', params: { id: confirmationId } })
      }

      return eventId
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.event_update_error'))
      isSubmitting.value = false
    }
  }

  // eslint-disable-next-line no-shadow
  const submit = (process, imgData, isDraft) => {
    isSubmitting.value = true
    stopTimer()

    let payload = {
      client_id: userData.client.$oid,
      process: String(process._id),
      worker: selectedWorker.value,
      supervisor: selectedSupervisor.value,
      activities: process.activities?.map((control, controlIndex) => ({
        name: control.name,
        population: parseInt(control.population || '0', 10),
        sample: sampleSizes.value[controlIndex] || 0,
        responsible_id: control.responsible_id ? oid(control.responsible_id) : null,
        responsibleLocked: !!control.responsibleLocked,
        behaviours: control.behaviours?.map((behaviour, behaviourIndex) => {
          if (behaviour.improvement) improvements.value.push(behaviour.improvement)

          if (behaviour.file?.length) {
            behaviour.file.forEach(file => {
              filesToUpload.value.push({
                file,
                clientId: userData.client.$oid,
                riskId: String(process._id),
                controlIndex,
                behaviourIndex,
              })
            })
          }

          return {
            name: behaviour.name,
            type: behaviour.type,
            answer: behaviour.answer?.toString() || '',
            expectedAnswer: behaviour.expectedAnswer,
            comment: behaviour.comment || '',
            evidenceKey: behaviour.evidenceKey || [],
          }
        }),
      })),
      duration: isNewConfirmation ? time.value : sumTimes(process.duration, time.value),
      metadata: process.metadata?.map(metadata => ({
        name: metadata.name,
        type: metadata.type,
        answer: metadata.answer,
      })),
      date: date(formatDate(event.value.date)),
      pending: isDraft,
      presenceValidated: isPresenceValidated.value,
      generalConclusion: generalConclusion.value,
      allFileKeys: process.allFileKeys || [],
    }

    if (isOnline.value) {
      postConfirmation(payload, imgData, isDraft)
    }
    else if (!isDraft) {
      if (route.value.params.event) {
        payload.eventId = route.value.params.event
        // TODO: use update method instead of get + set when localStorage is not used anymore
        get('events').then(response => {
          if (response) {
            for (let e of response) {
              if (e._id === payload.eventId) {
                e.extendedProps.calendar = "Realizadas"
                break
              }
            }
            set('events', response).then(() => {}).catch(error => console.log(error))
          } else {
            let eventsStored = localStorage.events ? JSON.parse(localStorage.events) : []
            for (let e of eventsStored) {
              if (e._id === payload.eventId) {
                e.extendedProps.calendar = "Realizadas"
                break
              }
            }
            localStorage.events = JSON.stringify(eventsStored)
          }
        })
      }
      payload.imgData = imgData
      payload.improvements = improvements.value
      storeDataToUpload('confirmationsToUpload', payload)
      // let confirmationsStored = localStorage.confirmations ? JSON.parse(localStorage.confirmations) : []
      // confirmationsStored.push({...payload})
      // localStorage.confirmations = JSON.stringify(confirmationsStored)
      showSuccessMessage(i18n.t('message.form_stored'))
      if (default_view_pc_team) router.push({ name: 'apps-pc-team' })
      else if (default_view_daily_dialogue) router.push({ name: 'habit-meeting-new' })
      else router.push({ name: 'apps-calendar' })
    }
  }

  const postConfirmation = async (payload, imgData, isDraft) => {
    payload.client_id = oid(payload.client_id)
    payload.process = oid(payload.process)
    payload.worker = oid(payload.worker)
    payload.supervisor = oid(payload.supervisor)

    try {
      // Calcular el nuevo RR antes de crear la confirmación (solo si no es borrador)
      if (!isDraft) {
        const rrCalculation = calculateRRForConfirmation(payload)
        if (rrCalculation) {
          payload.previousRR = rrCalculation.previousRR
          payload.newRR = rrCalculation.newRR
          payload.evaluationLevel = rrCalculation.level
          payload.compliancePercentage = rrCalculation.compliancePercentage
        }
      }

      // Upload files to AWS and then update the payload with the AWS file keys
      if (filesToUpload.value.length) {
        const uploadFilesPromises = filesToUpload.value.map(e => {
          const destinationFolder = `${e.clientId}/${e.riskId}/confirmation/${e.controlIndex}/${e.behaviourIndex}`
          return new Promise((resolve, reject) => {
            uploadFileToS3(e.file, destinationFolder)
              .then((key) => resolve({
                key,
                controlIndex: e.controlIndex,
                behaviourIndex: e.behaviourIndex
              }))
              .catch((err) => {
                console.log(err)
                showErrorMessage(i18n.t('message.evidence_upload_error'))
                resolve(null)
              })
          })
        })

        const newFileKeys = await Promise.all(uploadFilesPromises)

        for (const k of newFileKeys) {
          if (!k) continue
          if (payload.activities[k.controlIndex].behaviours[k.behaviourIndex].evidenceKey?.length) {
            payload.activities[k.controlIndex].behaviours[k.behaviourIndex].evidenceKey.push(k.key)
          } else {
            payload.activities[k.controlIndex].behaviours[k.behaviourIndex].evidenceKey = [k.key]
          }
        }

        const newFileKeysMapped = newFileKeys.map(k => k.key)
        payload.allFileKeys = [ ...payload.allFileKeys, ...newFileKeysMapped ]
      }

      const collection = 'confirmation'
      let confirmationId

      if (isNewConfirmation && !process.value.confirmationId) {
        const { insertedId } = await createItem({ collection, payload })
        if (!insertedId) throw new Error('Item not created')
        confirmationId = insertedId.toString()
      } else {
        // isReEvaluation usa esta misma rama (Confirmations Global "Re-evaluar") pero todavía
        // no tiene backend real de permisos (ver Convivencia en permisos-plan.md) — el chequeo
        // acá se limita al caso real de "Editar" desde Pending Confirmations.
        if (!isReEvaluation && !hasPermission('pending-confirmations', 'edit')) {
          showErrorMessage(i18n.t('message.you_are_not_authorized'))
          return
        }

        const query = { _id: oid(process.value.confirmationId) }
        if (clientId) query.client_id = oid(clientId)
        const action = { $set: payload }

        await updateItem({ collection, query, action })
        confirmationId = process.value.confirmationId
      }

      if (isDraft) {
        showSuccessMessage(i18n.t('message.confirmation_draft_saved'))
      } else {
        showSuccessMessage(i18n.t('message.confirmation_created'))

        // Upload image to AWS and then update the confirmation in MongoDB with the AWS image key
        const {fileInfo, destinationFolder} = imgData
        if (fileInfo) {
          uploadFileToS3(fileInfo, destinationFolder)
            .then((key) => updateConfirmationWithKey(confirmationId, key))
            .catch((err) => console.log(err))
        }

        // Actualizar el RR numérico y la categoría en el proceso si se calculó un nuevo RR
        if (payload.newRR !== undefined) {
          try {
            const newRRCategory = getRiskCategoryFromNumeric(payload.newRR)
            const processUpdateQuery = { _id: oid(payload.process) }
            const processUpdateAction = {
              $set: {
                residualRiskNumeric: payload.newRR,
                residualRisk: newRRCategory
              }
            }
            await updateItem({ collection: 'process', query: processUpdateQuery, action: processUpdateAction })
          } catch (error) {
            console.log('Error updating process RR:', error)
            // No lanzar error para no bloquear el flujo
          }
        }
      }

      updateEventWithConfirmation(confirmationId, isDraft)

      // Create cases from behaviours
      if (improvements.value.length) {
        improvements.value.forEach(e => {
          e.client_id = oid(e.client_id.link)
          e.confirmation = oid(confirmationId)
          e.assignee = oid(e.assignee)
          e.proposalResponsible = oid(e.assignee)
          e.approvalResponsible = e.approvalResponsible ? oid(e.approvalResponsible) : null
          e.verificationResponsible = e.verificationResponsible ? oid(e.verificationResponsible) : null
          e.dueDate = e.dueDate ? date(formatDate(e.dueDate)) : null
          e.createdAt = e.createdAt ? date(formatDate(e.createdAt)) : null
          e.proposalDueDate = e.proposalDueDate ? date(formatDate(e.proposalDueDate)) : null
          e.approvalDueDate = e.approvalDueDate ? date(formatDate(e.approvalDueDate)) : null
          e.verificationDueDate = e.verificationDueDate ? date(formatDate(e.verificationDueDate)) : null
          e.associatedCases = e.associatedCases ? e.associatedCases.map(a => oid(a)) : null
          e.areas = e.areas ? e.areas.map(a => oid(a)) : null
          e.stage = 'analysis'
          e.lastModifiedBy = userData?.worker_id?.$oid ? oid(userData.worker_id.$oid) : null,

          createCase(e)
        })
      }
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.confirmation_error'))
      isSubmitting.value = false
    }
  }

  const getEventId = async (confirmationId) => {
    try {
      const query = { confirmation: oid(confirmationId) }
      const item = await getItem({ collection: 'event', query })
      return item?._id?.toString()
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.event_update_error'))
    }
  }

  // Timer
  const startTimer = () => {      
    timeBegan.value = new Date()
    interval.value = setInterval(clockRunning, 1000)
  }
  
  const stopTimer = () => {
    clearInterval(interval.value)
  }
    
  const clockRunning = () => {
    const currentTime = new Date()
    , timeElapsed = new Date(currentTime - timeBegan.value)
    , hour = timeElapsed.getUTCHours()
    , min = timeElapsed.getUTCMinutes()
    , sec = timeElapsed.getUTCSeconds()
    // , ms = timeElapsed.getUTCMilliseconds()
    
    time.value = zeroPrefix(hour, 2) + ":" + zeroPrefix(min, 2) + ":" + zeroPrefix(sec, 2)
    // zeroPrefix(ms, 3);
  }
  
  const zeroPrefix = (num, digit) => {
    let zero = '';
    for(let i = 0; i < digit; i++) {
      zero += '0';
    }
    return (zero + num).slice(-digit);
  }

  const getPipeline = () => {
    const filter = { _id: oid(route.value.params.id) }

    return [
      { $match: filter },
      {
        $lookup: {
          from: "control",
          localField: "controls",
          foreignField: "_id",
          as: "activities",
          pipeline: [
            { $project: { name: 1, behaviours: 1, responsible: 1, responsible_id: 1 } },
            {
              $lookup: {
                from: 'behaviour',
                localField: 'behaviours',
                foreignField: '_id',
                pipeline: [ { $project: { name: 1, type: 1, expectedAnswer: 1, requiresEvidence: 1 } } ],
                as: 'behavioursData',
              },
            },
            {
              $lookup: {
                from: 'worker',
                localField: 'responsible_id',
                foreignField: '_id',
                as: 'responsibleWorkerData',
                pipeline: [ { $project: { name: 1 } } ],
              },
            },
            {
              $addFields: {
                behavioursData: {
                  $map: {
                    input: "$behaviours",
                    as: "behaviourId",
                    in: { $arrayElemAt: [ "$behavioursData", { $indexOfArray: ["$behavioursData._id", "$$behaviourId"] } ] }
                  }
                },
                // Nombre a mostrar (ej. getControlTitle): prioriza el worker de responsible_id, si no existe usa el texto libre viejo
                responsible: { $ifNull: [ { $arrayElemAt: ["$responsibleWorkerData.name", 0] }, "$responsible" ] }
              }
            },
            {
              $project: {
                name: 1,
                behaviours: "$behavioursData",
                responsible: 1,
                responsible_id: { $toString: "$responsible_id" }
              }
            }
          ]
        }
      },
      {
        $lookup: {
          from: "worker",
          localField: "evaluator",
          foreignField: "_id",
          as: "evaluator",
          pipeline: [ { $project: { name: 1, deleted: 1 } } ]
        }
      },
      {
        $lookup: {
          from: "worker",
          localField: "evaluated",
          foreignField: "_id",
          as: "evaluated",
          pipeline: [ { $project: { name: 1, deleted: 1 } } ]
        }
      },
      {
        $addFields: {
          activities: {
            $map: {
              input: "$controls",
              as: "controlId",
              in: { $arrayElemAt: [ "$activities", { $indexOfArray: ["$activities._id", "$$controlId"] } ] }
            }
          }
        }
      },
    ]
  }

  const getSampleSize = (inherentRisk, population) => {
    inherentRisk = inherentRisk || 'medium'
    population = population || 0
    const parsedPopulation = parseInt(population, 10);

    // ---- Validaciones básicas ----
    if (!Number.isFinite(parsedPopulation) || parsedPopulation < 0) {
      return 0;
    }

    const riskLevels = ['veryLow', 'low', 'medium', 'high', 'veryHigh'];
    const idx = riskLevels.indexOf(String(inherentRisk).trim());
    if (idx === -1) {
      return 0;
    }

    // ---- Parámetros según nivel de riesgo ----
    const highRisk          = inherentRisk === 'high' || inherentRisk === 'veryHigh';
    const Z                 = highRisk ? 1.96  : 1.645; // 95 % o 90 % de confianza
    const e                 = highRisk ? 0.10  : 0.15;  // margen de error
    const adjustmentFactor  = [0.75, 0.85, 1, 1.10, 1.20][idx];

    // ---- Cálculo de la muestra con corrección para población finita ----
    const p  = 0.5;            // Proporción más conservadora
    const q  = 1 - p;
    const Z2 = Z ** 2;

    const numerator   = parsedPopulation * Z2 * p * q;
    const denominator = (e ** 2) * (parsedPopulation - 1) + Z2 * p * q;
    const n0          = numerator / denominator;          // muestra base
    const sample      = Math.round(n0 * adjustmentFactor); // ajuste por riesgo

    // ---- La muestra nunca puede superar la población ----
    return Math.min(parsedPopulation, sample);
  }

  // Obtener comportamientos negativos
  const getNegativeBehaviours = () => {
    const negative = []
    process.value.activities?.forEach(activity => {
      activity.behaviours?.forEach(behaviour => {
        // Un comportamiento es negativo si la respuesta no coincide con la respuesta esperada
        if (behaviour.answer === "true" && !behaviour.expectedAnswer || behaviour.answer === "false" && behaviour.expectedAnswer) {
          negative.push({
            activityName: activity.name,
            behaviourName: behaviour.name,
            behaviourAnswer: behaviour.answer,
            behaviourComment: behaviour.comment || '',
          })
        }
      })
    })
    return negative
  }

  const createMultiBehaviourCase = async () => {
    try {
      isOpeningCaseForm.value = true
      const negativeBehaviours = getNegativeBehaviours()

      // Crear una confirmación borrador si es una nueva confirmación para asociar el confirmationId al caso generado
      if (isNewConfirmation && !tempEvaluationCreated) {
        const payload = { pending: true }
        const { insertedId } = await createItem({ collection: 'confirmation', payload })
        process.value.confirmationId = insertedId.toString()
        tempEvaluationCreated = true
      }

      const routeData = router.resolve({
        name: 'organization-case-add',
        query: {
          fromEvaluation: 'true',
          evaluationId: process.value.confirmationId,
          behaviours: JSON.stringify(negativeBehaviours)
        }
      })

      window.open(routeData.href, '_blank')
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.improvement_error'))
    } finally {
      isOpeningCaseForm.value = false
    }
  }

  /**
   * Calcula el nuevo RR basado en la evaluación para incluirlo en la confirmación
   * Retorna un objeto con previousRR, newRR, level, factor y compliancePercentage
   */
  const calculateRRForConfirmation = (confirmationPayload) => {
    try {
      // Usar los datos del proceso ya cargados en process.value
      if (!process.value?.residualRiskProbability || !process.value?.residualRiskImpact) {
        return null // Si no hay probabilidad e impacto, no se puede calcular el RR
      }

      // Determinar el RR anterior:
      // 1. Si el proceso tiene residualRiskNumeric (de evaluaciones previas), usar ese
      // 2. Si no, calcular desde probabilidad × impacto original
      let previousRRNumeric
      if (process.value.residualRiskNumeric) {
        previousRRNumeric = process.value.residualRiskNumeric
      } else {
        const probabilityValue = probabilityValues[process.value.residualRiskProbability]
        const impactValue = impactValues[process.value.residualRiskImpact]
        previousRRNumeric = probabilityValue * impactValue
      }

      // Calcular el porcentaje de cumplimiento
      let totalScore = 0
      let totalExpected = 0

      confirmationPayload.activities?.forEach(activity => {
        activity.behaviours?.forEach(behaviour => {
          if (behaviour.type === 'bool') {
            if (behaviour.answer === "true" || behaviour.answer === "false") {
              totalExpected += 1
            }
            if (behaviour.answer === behaviour.expectedAnswer?.toString()) {
              totalScore += 1
            }
          } else if (behaviour.type === 'rating') {
            if (behaviour.answer && behaviour.answer !== "N/A") {
              totalExpected += 1;
              totalScore += Math.round(((parseInt(behaviour.answer) / 5) * 10)) / 10;
            }
          }
        })
      })

      if (totalExpected === 0) return null // No hay comportamientos para evaluar

      const compliancePercentage = (totalScore / totalExpected) * 100

      // Calcular el nuevo RR
      const newRRResult = calculateNewRR(previousRRNumeric, compliancePercentage)

      if (!newRRResult) return null

      return {
        previousRR: previousRRNumeric,
        newRR: newRRResult.newRR,
        level: newRRResult.level,
        factor: newRRResult.factor,
        compliancePercentage
      }
    } catch (error) {
      console.log('Error calculating RR for confirmation:', error)
      return null
    }
  }

  // Función wrapper para schedulleEvent que envía emails de notificación al evaluador y evaluado
  const schedulleEventAndSendEmails = async () => {
    try {
      // Primero programa el evento
      const eventId = await schedulleEvent()

      // Si se creó el evento exitosamente, enviar emails
      if (eventId && selectedSupervisor.value && selectedWorker.value) {
        const processInfo = {
          _id: process.value._id,
          name: process.value.name,
          code: process.value.code,
        }

        // Obtener controles con responsables asignados (index = posición del control en el proceso)
        const controlsWithResponsibles = process.value.activities?.map((control, index) => ({
          name: control.name,
          responsible_id: control.responsible_id,
          index,
          skipNotification: !!control.skipNotification,
        })).filter(control => control.responsible_id) || []

        await sendScheduledEvaluationEmail({
          evaluatorId: selectedSupervisor.value,
          attendeeId: selectedWorker.value,
          processInfo,
          eventDate: event.value.date,
          eventId,
          controls: controlsWithResponsibles,
        })
      }
    } catch (error) {
      console.log('Error en schedulleEventAndSendEmails:', error)
    }
  }

  return {
    userData,
    show,
    submit,
    event,
    process,
    sampleSizes,
    selectedWorker,
    selectWorkerDisabled,
    selectedSupervisor,
    selectSupervisorDisabled,
    selectDateDisabled,
    schedulleEvent,
    schedulleEventAndSendEmails,
    enableConfirmation,
    saveControlResponsible,
    continueConfirmation,
    isSubmitting,
    postConfirmation,
    postEvent,
    interval,
    startTimer,
    stopTimer,
    metadataVisible,
    improvements,
    activatePresenceValidation,
    isSendingPresenceValidationEmail,
    isPresenceValidationEmailSent,
    validatePresence,
    isValidatingPresence,
    isPresenceValidated,
    generalConclusion,
    createMultiBehaviourCase,
    isOpeningCaseForm,
    emailSummary,
    showEmailSummaryModal,
    acknowledgeEmailSummary,
    respondAccessDenied,
    respondAlreadyCompleted,
    submitControlResponse,
    reloadControlsStatus,
    isReloadingControlsStatus,
  }
}
